<template>
  <v-card
    flat
    tile
  >
    <h4>Filtrer les {{ count.toLocaleString() }} éléments</h4>
    <v-text-field
      v-if="!config.hideTextFilter"
      v-model="search"
      :disabled="loading"
      label="Rechercher"
      append-icon="mdi-magnify"
      clearable
      @keyup.enter.native="refresh"
      @click:append="refresh"
      @input="refresh"
    />
    <template v-for="(filter, i) in config.dynamicFilters">
      <template v-if="metrics[filter.field.key] && (filter.field.type === 'number' || filter.field.type === 'integer')">
        <v-subheader
          :key="i+'sh'"
          class="pa-0"
          style="height:24px"
        >
          {{ label(filter.field.key) }}
        </v-subheader>
        <v-slider
          v-if="filter.forceOneValue"
          :key="i"
          v-model="filters[filter.field.key]"
          :min="metrics[filter.field.key].min"
          :max="metrics[filter.field.key].max"
          thumb-label
          class="px-2"
          @mouseup="refresh()"
        >
          <template v-slot:thumb-label="props">
            {{ props.value | abbreviate(metrics[filter.field.key].max - metrics[filter.field.key].min) }}
          </template>
        </v-slider>
        <v-range-slider
          v-else
          :key="i"
          v-model="filters[filter.field.key]"
          :min="metrics[filter.field.key].min"
          :max="metrics[filter.field.key].max"
          thumb-label
          class="px-2"
          @mouseup="refresh()"
        >
          <template v-slot:thumb-label="props">
            {{ props.value | abbreviate(metrics[filter.field.key].max - metrics[filter.field.key].min) }}
          </template>
        </v-range-slider>
      </template>
      <template v-else-if="filter.field.type === 'string' && filter.field.format === 'date' && metrics[filter.field.key]">
        <date-filter
          v-if="filter.forceOneValue"
          :key="i"
          v-model="filters[filter.field.key]"
          :field="filter.field"
          :min="metrics[filter.field.key].min"
          :max="metrics[filter.field.key].max"
          @input="refresh()"
        />
        <date-range-filter
          v-else
          :key="i"
          v-model="filters[filter.field.key]"
          :field="filter.field"
          :min="metrics[filter.field.key].min"
          :max="metrics[filter.field.key].max"
          @input="refresh()"
        />
      </template>
      <v-select
        v-else-if="enums && filter.facet"
        :key="i"
        v-model="filters[filter.field.key]"
        :dense="windowWidth <960"
        :label="label(filter.field.key)"
        :items="enums[filter.field.key]"
        :multiple="!filter.forceOneValue"
        :clearable="!filter.forceOneValue"
        @input="refresh()"
      />
      <v-autocomplete
        v-else-if="!filter.facet"
        :key="i"
        v-model="filters[filter.field.key]"
        :dense="windowWidth <960"
        :loading="loading"
        :label="label(filter.field.key)"
        :items="values[filter.field.key] || []"
        :filter="() => true"
        placeholder="Saisissez une valeur"
        hide-no-data
        :multiple="!filter.forceOneValue"
        :clearable="!filter.forceOneValue"
        @update:search-input="getValues(filter.field.key, $event)"
        @input="refresh()"
      >
        <template v-slot:item="data">
          <v-list-item-content v-html="filteredText(data.item.text, data.parent.searchInput)" />
        </template>
      </v-autocomplete>
    </template>
    <v-flex text-center>
      <v-btn
        :disabled="loading"
        color="primary"
        small
        @click.native="reset"
      >
        Réinitialiser
      </v-btn>
    </v-flex>
  </v-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import eventBus from '../assets/event-bus.js'
import DateRangeFilter from '@data-fair/components-app/date/DateRangeFilter'
import DateFilter from '@data-fair/components-app/date/DatePicker'
import { filters2qs, escape } from '@data-fair/components-app/filters/filters'
import axios from 'axios'
const debounce = require('debounce')

export default {
  components: {
    DateFilter,
    DateRangeFilter
  },
  data: () => ({
    filters: {},
    values: {},
    search: null,
    count: 0,
    enums: null,
    loading: false
  }),
  computed: {
    ...mapState(['dataset', 'metrics', 'conceptFilters']),
    ...mapGetters(['config', 'datasetUrl', 'fields']),
    query () {
      const query = Object.entries(this.params()).map(([key, val]) => `${key}=${encodeURIComponent(val)}`).join('&')
      return query && query.length ? '&' + query : ''
    }
  },
  watch: {
    filters: {
      handler (val) {
        const params = new URLSearchParams(window.location.search)
        for (const key in val) {
          if ((typeof val[key] !== 'object' && val[key]) || (val[key] && val[key].length)) {
            params.set(key, typeof val[key] !== 'object' ? val[key] : JSON.stringify(val[key]).slice(1, -1))
          } else params.delete(key)
        }
        window.history.replaceState(null, null, window.location.pathname + '?' + params.toString())
      },
      deep: true
    },
    search (val) {
      const params = new URLSearchParams(window.location.search)
      if (val && val.length) params.set('q', val)
      else params.delete('q')
      window.history.replaceState(null, null, window.location.pathname + '?' + params.toString())
    },
    '$route.query': {
      immediate: true,
      async handler () {
        this.filters = Object.assign({}, ...(this.config.dynamicFilters || []).map(filter => ({
          [filter.field.key]: filter.forceOneValue ? (filter.startValue) : []
        })))
        for (const field of Object.keys(this.filters)) {
          if (this.metrics[field]) {
            if (Array.isArray(this.filters[field])) this.filters[field] = [this.metrics[field].min, this.metrics[field].max]
            else if (!this.filters[field]) this.filters[field] = this.metrics[field].min
          }
        }
        const params = new URLSearchParams(window.location.search);
        (this.config.dynamicFilters || []).filter(filter => params.get(filter.field.key)).forEach(filter => {
          this.filters[filter.field.key] = filter.forceOneValue ? params.get(filter.field.key) : JSON.parse(`[${params.get(filter.field.key)}]`)
        })
        if (params.get('q')) this.search = params.get('q')
        await this.$nextTick()
        this.refresh()
      }
    }
  },
  methods: {
    params (ignoreField) {
      const params = { ...this.conceptFilters }
      const activeFilters = Object.keys(this.filters).filter(f => !ignoreField || ignoreField !== f).filter(field => !(Array.isArray(this.filters[field]) || typeof this.filters[field] === 'string') || this.filters[field].length)
      let qs = activeFilters.filter(field => this.metrics[field]).map(field => {
        if (Array.isArray(this.filters[field])) {
          return `${escape(field)}:[${escape(this.filters[field][0])} TO ${escape(this.filters[field][1] || this.filters[field][0])}]`
        } else return `${escape(field)}:${escape(this.filters[field])}`
      }).join(' AND ')
      if (activeFilters.filter(field => !this.metrics[field]).length) {
        qs += (qs.length ? ' AND ' : '') + activeFilters.filter(field => !this.metrics[field]).map(field => {
          if (Array.isArray(this.filters[field])) return `${escape(field)}:(${this.filters[field].map(f => `"${escape(f)}"`).join(' OR ')})`
          else return `${escape(field)}:"${escape(this.filters[field])}"`
        }).join(' AND ')
      }
      if (this.config.filters && this.config.filters.length) {
        qs += (qs.length ? ' AND ' : '') + filters2qs(this.config.filters)
      }
      params.qs = qs.length ? ('_exists_:_geopoint AND ' + qs) : '_exists_:_geopoint'
      if (this.search && this.search.length) params.q = this.search.trim().replace(/\s+/g, '+')
      params.finalizedAt = this.config.datasets[0].finalizedAt
      return params
    },
    refresh: debounce(async function () {
      this.$store.commit('setAny', { loading: true })
      this.count = (await axios.get(`${this.datasetUrl}/lines?size=0${this.query}`)).data.total
      const facets = (this.config.dynamicFilters || []).filter(filter => filter.facet)
      if (!this.enums) {
        this.enums = Object.assign({},
          ...facets.map(filter => ({ [filter.field.key]: this.fields[filter.field.key].enum || [] })
          ))
      }
      for (const filter of facets) {
        const params = { field: filter.field.key, agg_size: 1000, ...this.params(filter.field.key) }
        if (filter.sort === 'Alphabétique') params.sort = filter.field.key
        const results = (await axios.get(this.datasetUrl + '/values_agg', { params })).data
        const labels = this.fields[filter.field.key]['x-labels']
        this.enums[filter.field.key] = results.aggs.map(a => ({ text: `${labels ? labels[a.value] : a.value} (${a.total.toLocaleString('fr')})`, value: a.value }))
        if (filter.sort === 'Alphabétique') this.enums[filter.field.key].sort((a, b) => a.text.toLowerCase().localeCompare(b.text.toLowerCase()))
      }
      if (this.config.colors?.rangeOptions?.ranges && this.config.colors.rangeOptions.ranges.dynamic) {
        const metrics = (await axios.get(this.datasetUrl + `/simple_metrics_agg?fields:${this.config.colors.rangeOptions.field}${this.query}`)).data.metrics
        if (this.config.colors?.rangeOptions?.ranges && this.config.colors.rangeOptions.ranges.type === 'quantiles') {
          const percents = [0, ...Array.from({ length: this.config.colors.rangeOptions.ranges.size - 1 }, (_, i) => (i + 1) * (100 / this.config.colors.rangeOptions.ranges.size)), 100].join(',')
          const metrics = (await axios.get(this.datasetUrl + `/metric_agg?field=${this.config.colors.rangeOptions.field}${this.query}&metric=percentiles&percents=${percents}`)).data.metric
          this.$store.commit('setAny', { quantiles: metrics.map((m, i) => ({ min: i ? metrics[i - 1].value : undefined, max: m.value })).slice(1) })
        }
        this.$store.commit('setAny', { colorMetrics: metrics[this.config.colors.rangeOptions.field] })
      }
      eventBus.$emit('query', this.query)
    }, 500),
    label (key) {
      const field = this.dataset.schema.find(f => f.key === key)
      return field.title || field['x-originalName'] || field.key
    },
    async getValues (field, q) {
      this.loading = true
      if (!q || q.length > 2) {
        const params = { q, q_mode: 'complete' }
        if (this.config.filters && this.config.filters.length) {
          params.qs = filters2qs(this.config.filters)
        }
        const values = (await axios.get(this.datasetUrl + '/values/' + field, { params })).data
        const labels = this.fields[field]['x-labels']
        this.$set(this.values, field, values.map(v => ({ text: labels ? labels[v] : v, value: v })))
      }
      this.loading = false
    },
    filteredText (item, search) {
      if (!search) return item
      const nItem = item.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
      const nSearch = search.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
      const idx = nItem.indexOf(nSearch)
      if (idx < 0) return item
      const start = item.slice(0, idx)
      const middle = item.slice(idx, idx + search.length)
      const end = item.slice(idx + search.length)
      return `<div>${start}<span class="v-list-item__mask">${middle}</span>${end}</div>`
    },
    reset () {
      this.search = null
      this.filters = Object.assign({}, ...(this.config.dynamicFilters || []).map(filter => ({ [filter.field.key]: filter.forceOneValue ? filter.startValue : [] })))
      for (const field of Object.keys(this.filters)) {
        if (this.metrics[field] && Array.isArray(this.filters[field])) this.filters[field] = [this.metrics[field].min, this.metrics[field].max]
      }
      this.refresh()
      if (this.config.resetPosition) {
        eventBus.$emit('resetPosition')
      }
    }
  }
}
</script>
